import { useMutation } from "@apollo/client";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { BulkCustomerPhoneInput, UpdateCustomerInput } from "@/gql/graphql";
import { UPDATE_APPOINTMENT_MUTATION } from "@/graphql/appointments";
import {
  BULK_UPDATE_CUSTOMER_PHONES_MUTATION,
  UPDATE_CUSTOMER_MUTATION,
} from "@/graphql/customers";

interface EditCustomerParams {
  salonId: string;
  appointmentId: string;
  customerId: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string;
  originalPhone: string;
  // Include existing phones to prevent deletion
  existingPhones: Array<{ id: string; phone: string; isPrimary: boolean; label: string | null }>;
}

export function useEditCustomerForAppointment() {
  const { t } = useTranslation("appointments");
  const [updateCustomer] = useMutation(UPDATE_CUSTOMER_MUTATION);
  const [bulkUpdatePhones] = useMutation(BULK_UPDATE_CUSTOMER_PHONES_MUTATION);
  const [updateAppointment] = useMutation(UPDATE_APPOINTMENT_MUTATION);
  const [isUpdating, setIsUpdating] = useState(false);

  const editCustomer = async (params: EditCustomerParams): Promise<boolean> => {
    const {
      salonId,
      appointmentId,
      customerId,
      firstName,
      lastName,
      email,
      phone,
      originalPhone,
      existingPhones,
    } = params;

    setIsUpdating(true);
    try {
      const normalizedPhone = phone.trim();
      const normalizedOriginalPhone = originalPhone.trim();
      const phoneChanged = normalizedPhone !== normalizedOriginalPhone;

      // Step 1: Update customer basic info (name and email)
      const updateInput: UpdateCustomerInput = {
        firstName,
        lastName,
        email: email !== undefined ? email : undefined,
      };

      await updateCustomer({
        variables: {
          salonId,
          customerId,
          input: updateInput,
        },
      });

      // Step 2: Handle phone number changes
      if (phoneChanged) {
        // Deleting phone: clear appointment booking phone and remove the phone from the customer list
        if (!normalizedPhone) {
          const remainingPhones = existingPhones.filter((p) => p.phone !== normalizedOriginalPhone);

          const phonesList: BulkCustomerPhoneInput[] = remainingPhones.map((p, index) => ({
            id: p.id,
            phone: p.phone,
            isPrimary: index === 0,
            label: p.label || "mobile",
          }));

          await bulkUpdatePhones({
            variables: {
              salonId,
              customerId,
              input: {
                phones: phonesList,
              },
            },
          });

          await updateAppointment({
            variables: {
              salonId,
              appointmentId,
              input: {
                bookingPhoneId: null,
              },
            },
          });
        } else {
          // Build phones array: keep all existing phones + add/update the edited phone
          // The new phone will be set as primary, others will be set to non-primary
          const phonesList: BulkCustomerPhoneInput[] = [];

          // Check if the new phone already exists
          const existingPhoneRecord = existingPhones.find((p) => p.phone === normalizedPhone);

          if (existingPhoneRecord) {
            // Phone exists - update it to be primary
            phonesList.push({
              id: existingPhoneRecord.id,
              phone: existingPhoneRecord.phone,
              isPrimary: true,
              label: existingPhoneRecord.label || "mobile",
            });
          } else {
            // New phone - add it as primary
            phonesList.push({
              phone: normalizedPhone,
              isPrimary: true,
              label: "mobile",
            });
          }

          // Add all other existing phones as non-primary
          for (const existingPhone of existingPhones) {
            if (existingPhone.phone !== normalizedPhone) {
              phonesList.push({
                id: existingPhone.id,
                phone: existingPhone.phone,
                isPrimary: false,
                label: existingPhone.label || "mobile",
              });
            }
          }

          // Update customer phones - preserves all existing phones
          const result = await bulkUpdatePhones({
            variables: {
              salonId,
              customerId,
              input: {
                phones: phonesList,
              },
            },
          });

          // Get the updated/created phone ID
          const updatedPhones = result.data?.bulkUpdateCustomerPhones?.phoneNumbers;
          const newPhoneRecord = updatedPhones?.find((p) => p.phone === normalizedPhone);

          if (newPhoneRecord) {
            // Step 3: Update appointment's bookingPhoneId
            await updateAppointment({
              variables: {
                salonId,
                appointmentId,
                input: {
                  bookingPhoneId: newPhoneRecord.id,
                },
              },
            });
          }
        }
      }

      toast.success(t("messages.customerUpdated"));
      return true;
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("messages.customerUpdateFailed"));
      return false;
    } finally {
      setIsUpdating(false);
    }
  };

  return {
    editCustomer,
    isUpdating,
  };
}
